import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles, categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center px-4">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-2xl bg-secondary-100 text-secondary-500 mb-5">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h1 className="font-heading text-xl font-semibold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 text-sm mb-6">您访问的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.id === article.categoryId);
  const relatedArticles = articles
    .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
    .slice(0, 3);

  const contentHtml = article.content
    .replace(/^### (.+)$/gm, '<h3 class="font-heading text-lg font-semibold text-foreground-900 mt-8 mb-3">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 class="font-heading text-xl font-bold text-foreground-900 mt-10 mb-4">$1</h2>')
    .replace(/^#### (.+)$/gm, '<h4 class="font-heading text-base font-semibold text-foreground-800 mt-6 mb-2">$1</h4>')
    .replace(/^- (.+)$/gm, '<li class="ml-5 list-disc text-foreground-700 leading-relaxed mb-1">$1</li>')
    .replace(/^\d+\. (.+)$/gm, '<li class="ml-5 list-decimal text-foreground-700 leading-relaxed mb-1">$1</li>')
    .replace(/^\*\*(.+)\*\*$/gm, '<strong class="text-foreground-900">$1</strong>')
    .replace(/\n\n/g, '</p><p class="text-foreground-700 leading-relaxed mb-4">')
    .replace(/^(.+)$/gm, (match: string) => {
      if (match.startsWith('<')) return match;
      return match;
    });

  const finalHtml = `<p class="text-foreground-700 leading-relaxed mb-4">${contentHtml}</p>`;

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16">
        <article>
          <div className="w-full px-4 md:px-6 py-8 bg-background-100">
            <div className="max-w-[800px] mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', to: '/' },
                  { label: category?.name || '分类', to: `/category/${category?.slug}` },
                  { label: article.title },
                ]}
              />
            </div>
          </div>

          <div className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
            <div className="max-w-[800px] mx-auto">
              <div className="mb-8">
                <Link
                  to={`/category/${category?.slug}`}
                  className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1 rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors cursor-pointer mb-4 whitespace-nowrap"
                >
                  {category?.name}
                </Link>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 leading-tight mb-4">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                    {article.publishDate}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
                    {article.readTime}
                  </span>
                </div>
              </div>

              <div className="aspect-[16/9] rounded-xl overflow-hidden mb-10">
                <img
                  src={article.thumbnail}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div
                className="prose-content text-foreground-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: finalHtml }}
              />
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <div className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
              <div className="max-w-[1200px] mx-auto">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 text-center">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.slug}`}
                      className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all duration-200 cursor-pointer"
                    >
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={rel.thumbnail}
                          alt={rel.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-5">
                        <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-2 group-hover:text-primary-700 transition-colors line-clamp-2">
                          {rel.title}
                        </h3>
                        <p className="text-foreground-500 text-xs leading-relaxed line-clamp-2">
                          {rel.summary}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'Article',
                headline: article.title,
                description: article.summary,
                datePublished: article.publishDate,
                url: `https://guifeizaixian.cn/article/${article.slug}`,
                author: {
                  '@type': 'Organization',
                  name: 'www91官方导航',
                },
                publisher: {
                  '@type': 'Organization',
                  name: 'www91官方导航',
                },
                breadcrumb: {
                  '@type': 'BreadcrumbList',
                  itemListElement: [
                    {
                      '@type': 'ListItem',
                      position: 1,
                      name: '首页',
                      item: 'https://guifeizaixian.cn/',
                    },
                    {
                      '@type': 'ListItem',
                      position: 2,
                      name: category?.name || '分类',
                      item: `https://guifeizaixian.cn/category/${category?.slug}`,
                    },
                    {
                      '@type': 'ListItem',
                      position: 3,
                      name: article.title,
                    },
                  ],
                },
              }),
            }}
          />
        </article>
      </main>
      <Footer />
    </>
  );
}